import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/d967hl70d76aer3t514g";

export default function BookAppointment() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    // Honeypot check — silent success behavior
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setErrorMessage("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";

      if (
        !response.ok ||
        (parsed && parsed.code && parsed.code !== "OK") ||
        (typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam"))
      ) {
        setStatus("error");
        setErrorMessage(
          typeof serverMessage === "string" && serverMessage.trim().length > 0
            ? serverMessage
            : "Something went wrong. Please try again."
        );
        return;
      }

      setStatus("success");
      formEl.reset();
    } catch {
      setStatus("error");
      setErrorMessage("We couldn't reach the studio. Please try again.");
    }
  };

  return (
    <section
      id="book"
      className="relative w-full"
      style={{ backgroundColor: "#EFE7D6", color: "#1B1815" }}
    >
      <div className="relative z-10 max-w-[880px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <span className="w-8 h-px bg-[#1B1815]/60" />
          <span className="text-[10px] uppercase tracking-[0.4em] text-[#5A544C] whitespace-nowrap">
            Chapter Five · Get in Touch
          </span>
          <span className="w-8 h-px bg-[#1B1815]/60" />
        </div>

        <h2
          className="font-black uppercase leading-[0.9] tracking-[-0.03em] text-center"
          style={{ fontSize: "clamp(40px, 5.6vw, 80px)" }}
        >
          LET&apos;S{" "}
          <span className="italic font-light lowercase tracking-tight text-[#5A544C]">
            talk about
          </span>{" "}
          YOUR ROOM.
        </h2>

        <p className="mt-6 text-base md:text-lg leading-relaxed text-[#3A342E] max-w-lg mx-auto text-center">
          A first conversation is quiet and free. Elin replies personally,
          within two working days.
        </p>

        <form
          onSubmit={handleSubmit}
          data-readdy-form
          className="mt-14 md:mt-16"
        >
          {/* Honeypot */}
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="hp-field"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-[0.35em] text-[#5A544C] mb-2">
                Name
              </label>
              <input
                type="text"
                name="name"
                required
                maxLength={80}
                className="bg-transparent border-b border-[#1B1815]/30 focus:border-[#8A6C3F] outline-none py-2 text-[15px] text-[#1B1815] placeholder:text-[#8A6C3F]/40"
                placeholder="First and last"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-[0.35em] text-[#5A544C] mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                required
                maxLength={120}
                className="bg-transparent border-b border-[#1B1815]/30 focus:border-[#8A6C3F] outline-none py-2 text-[15px] text-[#1B1815] placeholder:text-[#8A6C3F]/40"
                placeholder="you@studio.com"
              />
            </div>
          </div>

          <div className="mt-8 flex flex-col">
            <label className="text-[10px] uppercase tracking-[0.35em] text-[#5A544C] mb-2">
              About the room
            </label>
            <textarea
              name="message"
              required
              maxLength={500}
              rows={4}
              className="bg-transparent border-b border-[#1B1815]/30 focus:border-[#8A6C3F] outline-none py-2 text-[15px] text-[#1B1815] placeholder:text-[#8A6C3F]/40 resize-none"
              placeholder="A few sentences on the space and the feeling you want…"
            />
          </div>

          <div className="mt-12 flex justify-center">
            <button
              type="submit"
              disabled={status === "submitting"}
              className="group inline-flex items-center justify-center gap-3 bg-[#1B1815] hover:bg-[#3A342E] text-[#F5EFE4] text-[13px] uppercase tracking-[0.3em] px-10 py-4 rounded-full cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "submitting" ? "Sending…" : "Send"}
              <i className="ri-arrow-right-up-line text-[16px] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          {status === "success" && (
            <div
              className="mt-10 text-[14px] text-[#1B1815] leading-relaxed text-center"
              role="status"
            >
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#8A6C3F] mb-2">
                Received
              </div>
              Thank you — your note is with us.
            </div>
          )}

          {status === "error" && (
            <div
              className="mt-10 text-[14px] text-[#8A3F1B] leading-relaxed text-center"
              role="alert"
            >
              {errorMessage}
            </div>
          )}
        </form>

        <div className="mt-16 pt-10 border-t border-[#1B1815]/10 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-[12px] text-[#5A544C]">
          <a
            href="mailto:studio@elinvahr.com"
            className="hover:text-[#1B1815] transition-colors cursor-pointer"
          >
            studio@elinvahr.com
          </a>
          <span className="hidden md:inline w-px h-3 bg-[#1B1815]/20" />
          <span>Copenhagen · Lisbon</span>
        </div>
      </div>
    </section>
  );
}